import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, Eye, EyeOff, Radio } from "lucide-react";
import { toast } from "sonner";

export function AdminPrompts() {
  const [cats, setCats] = useState([]);

  const load = () => api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patchCat = (id, patch) => setCats((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const patchPrompt = (id, pi, patch) =>
    setCats((cs) => cs.map((c) => (c.id === id ? { ...c, prompts: c.prompts.map((p, j) => (j === pi ? { ...p, ...patch } : p)) } : c)));
  const addPrompt = (id) =>
    setCats((cs) => cs.map((c) => (c.id === id ? { ...c, prompts: [...c.prompts, { text: "", paired_value: "", level: c.level }] } : c)));
  const removePrompt = (id, pi) =>
    setCats((cs) => cs.map((c) => (c.id === id ? { ...c, prompts: c.prompts.filter((_, j) => j !== pi) } : c)));

  const save = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, {
        name: c.name, level: c.level,
        prompts: c.prompts.filter((p) => (p.text || "").trim()).map((p) => ({ text: p.text, paired_value: p.paired_value || null, level: p.level || c.level })),
      });
      toast.success(`Saved "${c.name}"`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const togglePublish = async (c) => {
    try {
      const { data } = await api.post(`/admin/categories/${c.id}/publish`, { published: !c.published });
      patchCat(c.id, { published: data.published });
      toast.success(data.published ? `"${c.name}" is now live in the generator` : `"${c.name}" unpublished (draft)`);
    } catch { toast.error("Could not update publish status"); }
  };

  const removeCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}" and all its prompts?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      setCats((cs) => cs.filter((x) => x.id !== c.id));
      toast.success(`Deleted "${c.name}"`);
    } catch { toast.error("Could not delete"); }
  };

  const drafts = cats.filter((c) => !c.published).length;

  return (
    <div data-testid="admin-prompts">
      <p className="text-[#545454] mb-4">
        {cats.length} categories · <span className="font-semibold text-[#b45309]">{drafts} draft{drafts === 1 ? "" : "s"}</span> awaiting review.
        Drafts are hidden from the generator until you publish them.
      </p>
      <div className="space-y-5">
        {cats.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-[#dfd8da] overflow-hidden" data-testid={`prompt-cat-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3 p-4 bg-[#f9f6f6] border-b border-[#dfd8da]">
              <input value={c.name} onChange={(e) => patchCat(c.id, { name: e.target.value })} data-testid={`cat-name-${c.id}`}
                className="font-bold text-lg text-[#1a1617] bg-transparent border-b border-transparent focus:border-[#920e12] px-1 min-w-[10rem]" />
              <span className="text-sm text-[#545454]">Level</span>
              <input value={String(c.level)} onChange={(e) => patchCat(c.id, { level: e.target.value })} data-testid={`cat-level-${c.id}`}
                className="w-14 text-center rounded-md border border-[#d6cecf] px-2 py-1" />
              {c.published ? (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#dcfce7] text-[#15803d]" data-testid={`cat-status-${c.id}`}>Live</span>
              ) : (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#fef3c7] text-[#b45309]" data-testid={`cat-status-${c.id}`}>Draft</span>
              )}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => togglePublish(c)} data-testid={`cat-publish-${c.id}`}
                  className={c.published ? "border-[#b45309] text-[#b45309]" : "border-[#15803d] text-[#15803d]"}>
                  {c.published ? <><EyeOff size={14} className="mr-1" /> Unpublish</> : <><Radio size={14} className="mr-1" /> Publish</>}
                </Button>
                <Button size="sm" onClick={() => save(c)} data-testid={`cat-save-${c.id}`} className="bg-[#920e12] hover:bg-[#550001] text-white">
                  <Save size={14} className="mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => removeCategory(c)} data-testid={`cat-delete-${c.id}`} className="border-[#991b1b] text-[#991b1b]">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wide font-bold text-[#545454] px-1">
                <div className="col-span-5">Prompt</div>
                <div className="col-span-6">Paired value (e.g. the Problem)</div>
                <div className="col-span-1"></div>
              </div>
              {c.prompts.map((p, pi) => (
                <div key={pi} className="grid grid-cols-12 gap-2 items-center" data-testid={`cat-prompt-${c.id}-${pi}`}>
                  <input value={p.text} onChange={(e) => patchPrompt(c.id, pi, { text: e.target.value })} data-testid={`cat-prompt-text-${c.id}-${pi}`}
                    className="col-span-5 h-9 px-2 rounded-md border border-[#d6cecf]" placeholder="Prompt text" />
                  <input value={p.paired_value || ""} onChange={(e) => patchPrompt(c.id, pi, { paired_value: e.target.value })} data-testid={`cat-prompt-pair-${c.id}-${pi}`}
                    className="col-span-6 h-9 px-2 rounded-md border border-[#d6cecf]" placeholder="(optional)" />
                  <button onClick={() => removePrompt(c.id, pi)} data-testid={`cat-prompt-remove-${c.id}-${pi}`}
                    className="col-span-1 text-[#991b1b] hover:bg-[#fee2e2] rounded-md h-9 flex items-center justify-center" aria-label="Remove prompt">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addPrompt(c.id)} data-testid={`cat-prompt-add-${c.id}`} className="mt-2 border-[#920e12] text-[#920e12]">
                <Plus size={14} className="mr-1" /> Add prompt
              </Button>
            </div>
          </div>
        ))}
        {cats.length === 0 && <p className="text-[#545454]">No categories yet — import some from the Bulk Import tab.</p>}
      </div>
    </div>
  );
}
